import React from "react";
import {
    PieChart,
    Pie,
    Cell,
    ResponsiveContainer,
    Tooltip
} from "recharts";

export interface DonutItem {
    id: string;           
    name: string;
    y: number;
    color: string;
    handle?: string;
    avatar?: string;
}

const DonutChart: React.FC<{
    title: string;
    data: DonutItem[];
    hoveredId?: string | null;
    showLegend?: boolean;
    tooltipRenderer?: (ctx: {
        name: string;
        value: number;
        color: string;
        handle?: string;
        avatar?: string;
        metricLabel: string;
    }) => React.ReactNode;
}> = ({ title, data, tooltipRenderer, hoveredId, showLegend }) => {
    const chartData = data.map(d => ({
        id: d.id,          
        name: d.name,
        value: d.y,
        color: d.color,
        handle: d.handle,
        avatar: d.avatar
    }));

    const legendColumnCount = chartData.length <= 2 ? 2 : 3;
    /** 2-col: content-sized tracks so Male/Female group tight + center under donut (avoid 50/50 stretched columns). */
    const legendListStyle: React.CSSProperties =
        legendColumnCount === 2
            ? {
                  display: "inline-grid",
                  gridTemplateColumns: "max-content max-content",
                  columnGap: 24,
                  rowGap: 4,
                  justifyItems: "start",
                  justifyContent: "center",
                  verticalAlign: "top",
                  maxWidth: "100%",
                  padding: 0,
                  margin: 0,
                  listStyle: "none",
              }
            : {
                  display: "grid",
                  gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                  columnGap: 16,
                  rowGap: 4,
                  justifyItems: "start",
                  width: "min(100%, 420px)",
                  maxWidth: 420,
                  padding: 0,
                  margin: 0,
                  listStyle: "none",
              };

    const legendItemStyle: React.CSSProperties = {
        display: "flex",
        alignItems: "center",
        gap: 6,
        fontSize: 12,
        color: "#374151",
        minWidth: 0,
    };

    const legendLabelPctStyle: React.CSSProperties = {
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        minWidth: 0,
        maxWidth: "100%",
    };

    const swatchStyle: React.CSSProperties = {
        width: 10,
        height: 10,
        borderRadius: 2,
        flexShrink: 0,
    };

    const wrapStyle: React.CSSProperties = showLegend
        ? {
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 10,
              width: "100%",
          }
        : {};

    const legendTotal = chartData.reduce((sum, entry) => sum + (Number(entry.value) || 0), 0);

    return (
        <div style={wrapStyle}>
            <div style={{ width: 200, height: 200, position: "relative", flexShrink: 0 }}>
                <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                        <Pie
                            data={chartData}
                            dataKey="value"
                            cx="50%"
                            cy="50%"
                            innerRadius="70%"
                            outerRadius="100%"
                            paddingAngle={1}
                            isAnimationActive
                        >
                            {chartData.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={entry.color} opacity={hoveredId && entry.id !== hoveredId ? 0.3 : 1} />
                            ))}
                        </Pie>

                        <Tooltip
                            wrapperStyle={{ zIndex: 9999 }}
                            content={(props: any) => {
                                if (!tooltipRenderer) return null;
                                if (!props.active || !props.payload || !props.payload.length) {
                                    return null;
                                }

                                const item = props.payload[0].payload;

                                return tooltipRenderer({
                                    name: item.name,
                                    value: item.value,
                                    color: item.color,
                                    handle: item.handle,
                                    avatar: item.avatar,
                                    metricLabel: title
                                });
                            }}
                        />
                    </PieChart>
                </ResponsiveContainer>

                {/* Center Label */}
                <div
                    style={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        textAlign: "center",
                        pointerEvents: "none"
                    }}
                >
                    <div style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>
                        {title}
                    </div>
                </div>
            </div>

            {showLegend && chartData.length > 0 && (
                <ul style={legendListStyle}>
                    {chartData.map((entry) => {
                        const pct =
                            legendTotal > 0
                                ? ((Number(entry.value) || 0) / legendTotal) * 100
                                : 0;
                        return (
                            <li key={entry.id} style={legendItemStyle}>
                                <span style={{ ...swatchStyle, backgroundColor: entry.color }} aria-hidden />
                                <span style={legendLabelPctStyle}>
                                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 120 }}>
                                        {entry.name}
                                    </span>
                                    <span
                                        style={{
                                            color: "#6b7280",
                                            fontWeight: 500,
                                            fontVariantNumeric: "tabular-nums",
                                            flexShrink: 0,
                                        }}
                                    >
                                        {pct.toFixed(1)}%
                                    </span>
                                </span>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
};

export default DonutChart;
